import { Rotate } from '@unistocks/components/animations/rotate';
import {
  AnimatedScrollView,
  AnimatedScrollViewProps,
  ScrollableRef
} from '@unistocks/components/basic/scrollview';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import {
  COLLAPSED_HEADER,
  EXPANDED_HEADER,
  MAX_PULL_DOWN,
  RESISTANCE_FACTOR,
  RESISTANCE_START,
  ROW_HEIGHT
} from '@unistocks/components/page/consts';
import { PageHeader } from '@unistocks/components/page/header/page-header';
import { safeScrollToTop } from '@unistocks/components/page/scrollview/safe-scroll-to-top';
import { QueryState, QueryStateProps } from '@unistocks/components/query-state/query-state';
import {
  AnimatedVirtualList,
  AnimatedVirtualListProps,
  VirtualListProps,
  VirtualListRef
} from '@unistocks/components/virtual-list/list';
import { Route } from '@unistocks/features/navigation/route';
import { quicker } from '@unistocks/shared/animations';
import { useStartPage } from '@unistocks/shared/hooks/use-start-page';
import { getComponentDisplayName } from '@unistocks/shared/utils/get-display-name';
import { triggerHaptics } from '@unistocks/shared/utils/haptics/haptics';
import { resistance } from '@unistocks/shared/utils/resistance';
import { isFunction } from '@unistocks/shared/utils/util';
import { RefProp } from '@unistocks/types';
import { useRouter } from 'expo-router';
import { useScrollToTop } from 'expo-router/react-navigation';
import { Key, useCallback, useEffect, useRef } from 'react';
import { RefreshControlProps, ScrollViewProps, View } from 'react-native';
import { Gesture, GestureDetector, PanGesture } from 'react-native-gesture-handler';
import Animated, {
  AnimatedRef,
  Extrapolation,
  interpolate,
  useAnimatedRef,
  useAnimatedStyle,
  useScrollOffset,
  useSharedValue,
  withSpring
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { scheduleOnRN } from 'react-native-worklets';
import { isWeb, isWebTouchable, useComposedRefs, useControllableState } from 'tamagui';

export type HeaderProps = {
  /**
   * Show page header title. Can be used with or without the logo.
   */
  headerTitle?: React.ReactNode;
  /**
   * Show page header subtitle. Can be used together with title, without the logo.
   */
  headerSubtitle?: React.ReactNode;
  /**
   * Show page header icon on the right side
   */
  headerIcon?: React.ReactNode;
  /**
   * Show logo in the header. If true, the logo will be shown centered on the top of the page and the title below.
   */
  headerLogo?: boolean;
  /**
   * Show back arrow in the header. If true, the back arrow will be shown on the left side of the header.
   * @default true
   */
  headerBackArrow?: boolean;
  /**
   * Show notification icon in the header
   * @default undefined show notification icon when the logo is shown
   */
  headerNotificationIcon?: boolean;
  /**
   * If true, the header will remain transparent when the page is scrolling down.
   * If false, the header will become opaque when the page is scrolled down.
   * @default false
   */
  headerTransparent?: boolean;

  /**
   * Breadcrumb routes
   */
  breadcrumbs?: Route[];
};

type ComponentProps = Omit<AnimatedScrollViewProps & AnimatedVirtualListProps, 'key' | 'ref'>;
type Props = ComponentProps &
  RefreshControlProps &
  HeaderProps & {
    queryState?: QueryStateProps;
  } & RefProp<Animated.ScrollView> & { key?: Key };

function withAnimatedHeader<T extends typeof AnimatedScrollView | typeof AnimatedVirtualList>(
  ScrollComponent: T,
  {
    contentContainerStyle: contentContainerStyleDefaults,
    wrapInHostView = false,
    ...overrideProps
  }: Partial<
    T extends typeof AnimatedScrollView ? AnimatedScrollViewProps : AnimatedVirtualListProps
  > & { wrapInHostView?: boolean } = {}
): React.ComponentType<any> {
  const WithAnimatedHeader = ({
    contentContainerStyle,
    headerTitle: title,
    headerSubtitle: subtitle,
    headerIcon: icon,
    headerLogo: logo = true,
    headerBackArrow: showBackArrow,
    headerNotificationIcon: showNotificationIcon,
    headerTransparent,
    breadcrumbs,
    refreshing: refreshingProp,
    onRefresh,
    progressViewOffset,
    queryState,
    ref: refProp,
    children,
    ...props
  }: Props) => {
    const Component = ScrollComponent as React.ComponentType<any>;
    const { top } = useSafeAreaInsets();
    const router = useRouter();
    const startPage = useStartPage();
    const gestureRef = useRef<PanGesture>(undefined);
    const scrollRef = useAnimatedRef<Animated.ScrollView>();
    const scrollOffset = useScrollOffset(scrollRef);
    const composedRef = useComposedRefs<Animated.ScrollView>(scrollRef, refProp);
    useScrollToTop(scrollRef);

    const initialScrollOffset = useSharedValue(0);
    const scrollEnabled = useSharedValue(true);
    const [refreshing, setRefreshing] = useControllableState({
      strategy: 'most-recent-wins',
      prop: refreshingProp,
      defaultProp: false,
      onChange(next: boolean) {
        if (next) {
          onRefresh?.();
          safeScrollToTop(scrollRef);
          pullDownPosition.value = withSpring(MAX_PULL_DOWN / 2, quicker);
        }
      }
    });

    const pullDownPosition = useSharedValue(0);
    const willRefresh = useSharedValue(false);
    const animatedStyle = useAnimatedStyle(() => ({
      flex: 1,
      paddingTop: isWeb
        ? 2
        : interpolate(
            scrollOffset.value,
            [COLLAPSED_HEADER, EXPANDED_HEADER],
            // Collapsed header plus top is how far the header reaches from the screen top. allow some space for the fade out effect
            [0, ROW_HEIGHT - top + 10],
            Extrapolation.CLAMP
          )
    }));

    const pullDownStyle = useAnimatedStyle(() => ({
      transform: [
        {
          translateY: pullDownPosition.value
        }
      ]
    }));

    const refreshIconStyle = useAnimatedStyle(() => ({
      opacity: interpolate(
        pullDownPosition.value,
        [0, MAX_PULL_DOWN / 2],
        [0, 1],
        Extrapolation.CLAMP
      ),
      transform: [
        {
          rotate: `${interpolate(
            pullDownPosition.value,
            [0, MAX_PULL_DOWN],
            [0, 180],
            Extrapolation.CLAMP
          )}deg`
        },
        {
          scale: interpolate(
            pullDownPosition.value,
            [0, MAX_PULL_DOWN],
            [0.5, 2],
            Extrapolation.CLAMP
          )
        }
      ]
    }));

    useEffect(() => {
      if (!refreshing) {
        pullDownPosition.value = withSpring(0, quicker);
      }
    }, [pullDownPosition, refreshing]);

    const getNativeScrollRef = () => {
      let scrollable = scrollRef.current as any;

      if (!scrollable) return null;

      if (!('setNativeProps' in scrollable)) {
        // When using FlashList we need to get the underlying ScrollView
        scrollable = scrollable?.getNativeScrollRef();
      }

      return scrollable;
    };

    const setScrollEnabled = (next: boolean) => {
      if (scrollEnabled.value === next) return;

      getNativeScrollRef()?.setNativeProps?.({
        scrollEnabled: next
      });

      scrollEnabled.value = next;
    };

    // Pan gesture for pull-to-refresh when at the top of the scroll view
    const panGesture = Gesture.Pan()
      .withRef(gestureRef)
      .enabled(!refreshing && isFunction(onRefresh))
      .activeOffsetY(10)
      .onStart(() => {
        initialScrollOffset.value = scrollOffset.value;
      })
      .onUpdate(event => {
        const rawPullDown = Math.max(event.translationY - initialScrollOffset.value, 0);

        if (scrollOffset.value <= 0 && event.translationY > 0) {
          const pullDown = resistance(
            rawPullDown,
            MAX_PULL_DOWN * RESISTANCE_START,
            RESISTANCE_FACTOR
          );
          pullDownPosition.value = pullDown;
          scheduleOnRN(setScrollEnabled, false);
        } else if (rawPullDown === 0) {
          pullDownPosition.value = 0;
          scheduleOnRN(setScrollEnabled, true);
        }

        const readyToRefresh = pullDownPosition.value >= MAX_PULL_DOWN * 0.75;

        if (!willRefresh.value && readyToRefresh) scheduleOnRN(triggerHaptics, 'gestureStart');
        else if (willRefresh.value && !readyToRefresh) scheduleOnRN(triggerHaptics, 'gestureEnd');

        willRefresh.value = readyToRefresh;
      })
      .onEnd(() => {
        if (willRefresh.value) {
          scheduleOnRN(setRefreshing, true);
        } else {
          pullDownPosition.value = withSpring(0, quicker);
        }
        initialScrollOffset.value = 0;
        willRefresh.value = false;
        scheduleOnRN(setScrollEnabled, true);
      });

    const onLogoPress = useCallback(() => {
      if (scrollOffset.value === 0) {
        router.navigate(startPage);
      } else {
        safeScrollToTop(scrollRef);
      }
    }, [scrollOffset, scrollRef, router, startPage]);

    // const hasHeader = logo || title || subtitle || icon || showBackArrow || showNotificationIcon;

    const wrappedChildren = queryState ? (
      <QueryState mt={'$10'} {...queryState}>
        {children}
      </QueryState>
    ) : (
      children
    );

    const scrollComponent = (
      <Component
        ref={composedRef}
        overScrollMode={'never'}
        fadingEdgeLength={20}
        keyboardShouldPersistTaps={'handled'}
        showsVerticalScrollIndicator={isWebTouchable}
        scrollEventThrottle={16}
        {...props}
        contentContainerStyle={{
          ...(contentContainerStyleDefaults as any),
          ...(contentContainerStyle as any),
          paddingTop: isWeb ? undefined : EXPANDED_HEADER
        }}
        /* Difference between ScrollView and FlashList */
        simultaneousHandlers={gestureRef}
        overrideProps={{
          simultaneousHandlers: gestureRef
        }}
        {...overrideProps}
      >
        {wrappedChildren}
      </Component>
    );

    return (
      <Animated.View style={[animatedStyle, pullDownStyle]}>
        {!isWeb ? (
          <Rotate enabled={refreshing}>
            <UnistocksCircle mx={'auto'} style={refreshIconStyle} />
          </Rotate>
        ) : null}
        <PageHeader
          transparent={headerTransparent}
          scrollOffset={scrollOffset}
          showBackArrow={showBackArrow}
          showNotificationIcon={showNotificationIcon}
          logo={logo}
          onLogoPress={onLogoPress}
          title={title}
          subtitle={subtitle}
          icon={icon}
          breadcrumbs={breadcrumbs}
        />
        <GestureDetector gesture={panGesture}>
          {wrapInHostView ? (
            <View collapsable={false} style={{ flex: 1 }}>
              {scrollComponent}
            </View>
          ) : (
            scrollComponent
          )}
        </GestureDetector>
      </Animated.View>
    );
  };

  WithAnimatedHeader.displayName = `withAnimatedHeader(${getComponentDisplayName(
    ScrollComponent as any
  )})`;

  return WithAnimatedHeader;
}

// ScrollView
export const PAGE_SCROLL_VIEW_NAME = 'PageScrollView';

export type PageScrollViewRef = ScrollableRef;
type PageScrollViewProps = Omit<ScrollViewProps, 'refreshControl'> &
  Partial<Pick<RefreshControlProps, 'onRefresh' | 'refreshing'>> & {
    queryState?: QueryStateProps;
  };

export const PageScrollView = withAnimatedHeader(AnimatedScrollView, {
  mx: '$-1.5',
  contentContainerStyle: {
    px: '$1.5'
  }
}) as React.ComponentType<PageScrollViewProps & HeaderProps & { ref?: PageScrollViewRef }>;

(PageScrollView as React.FC).displayName = PAGE_SCROLL_VIEW_NAME;

// FlashList
export const PAGE_FLASH_LIST_NAME = 'PageVirtualList';

export type PageVirtualListRef<T> = AnimatedRef<VirtualListRef<T>>;

export const PageVirtualList = withAnimatedHeader(AnimatedVirtualList, {
  mx: '$-1.5',
  contentContainerStyle: {
    px: '$1.5'
  },
  wrapInHostView: true
}) as <T>(
  props: VirtualListProps<T> & HeaderProps & { ref?: PageVirtualListRef<T> }
) => React.JSX.Element;

(PageVirtualList as React.FC).displayName = PAGE_FLASH_LIST_NAME;
